import { useCallback, useRef } from "react";

interface UseAutoScrollToContentOptions {
	scrollContainer: React.RefObject<HTMLElement> | HTMLElement | null;
	enabled?: boolean;
	offset?: number;
	behavior?: ScrollBehavior;
	delay?: number;
}

interface UseAutoScrollToContentReturn {
	scrollToElement: (element: HTMLElement | null) => void;
	scrollToRevealedContent: (toggleElement: HTMLElement | null, contentElement?: HTMLElement | null) => void;
	isContentVisible: (element: HTMLElement | null) => boolean;
}

export const useAutoScrollToContent = ({
	scrollContainer,
	enabled = true,
	offset = 20,
	behavior = "smooth",
	delay = 100,
}: UseAutoScrollToContentOptions): UseAutoScrollToContentReturn => {
	const timeoutRef = useRef<NodeJS.Timeout>();

	const getScrollContainer = useCallback((): HTMLElement | null => {
		if (!scrollContainer) return null;
		if ("current" in scrollContainer) return scrollContainer.current;
		return scrollContainer;
	}, [scrollContainer]);

	const isContentVisible = useCallback(
		(element: HTMLElement | null): boolean => {
			if (!element || !enabled) return false;

			const container = getScrollContainer();
			if (!container) return false;

			const containerRect = container.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();

			return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
		},
		[enabled, getScrollContainer],
	);

	const scrollToElement = useCallback(
		(element: HTMLElement | null) => {
			if (!element || !enabled) return;

			const container = getScrollContainer();
			if (!container) return;

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				const containerRect = container.getBoundingClientRect();
				const elementRect = element.getBoundingClientRect();

				const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - offset;

				container.scrollTo({
					top: scrollTop,
					behavior,
				});
			}, delay);
		},
		[enabled, getScrollContainer, offset, behavior, delay],
	);

	const scrollToRevealedContent = useCallback(
		(toggleElement: HTMLElement | null, contentElement?: HTMLElement | null) => {
			if (!enabled) return;

			const targetElement = contentElement || (toggleElement?.nextElementSibling as HTMLElement);

			if (!targetElement) return;

			if (isContentVisible(targetElement)) return;

			scrollToElement(targetElement);
		},
		[enabled, isContentVisible, scrollToElement],
	);

	return {
		scrollToElement,
		scrollToRevealedContent,
		isContentVisible,
	};
};
